"use client";

import React, { useState, useEffect } from 'react';
import { Separator } from "@/components/ui/separator";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { Calendar, Trash, Clock, X, Check } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Status, StatusIndicator } from '@/components/ui/shadcn-io/status';
import axiosInstance from '@/api/axios';
import { useTranslation } from 'react-i18next';
import CustomCalendar from "@/common/Components/Calendar/Calendar";

const AVAILABLE_BINS = ['R1', 'R2', 'R3', 'R4', 'R5', 'R6', 'R7', 'R9', 'R13', 'R14', 'R15', 'R17', 'IST01', 'IST03', 'B1', 'B2', 'B3'];
const TIME_PERIODS = ['day', 'week', 'month', '3months', '6months', 'year'];

// Phone-style battery indicator — body + terminal nub, fill bar scales with
// percentage, colored by the backend's status_color (red/orange/green).
// Copied from operations SiteHeader so both bin dropdowns render identically.
const BatteryBadge = ({ percentage, color }) => {
  const fillColor = color === 'red' ? '#ef4444' : color === 'orange' ? '#f97316' : '#22c55e';
  const pct = Math.max(0, Math.min(100, percentage));

  const bodyWidth  = 32;
  const bodyHeight = 16;
  const nubWidth   = 2.5;
  const nubHeight  = 7;
  const padding    = 1.6;
  const innerWidth  = bodyWidth - padding * 2;
  const innerHeight = bodyHeight - padding * 2;
  const fillWidth   = (innerWidth * pct) / 100;

  return (
    <div className="group relative flex items-center justify-center">
      <svg
        width={bodyWidth + nubWidth}
        height={bodyHeight}
        viewBox={`0 0 ${bodyWidth + nubWidth} ${bodyHeight}`}
        className="transition-transform duration-150 group-hover:scale-110"
      >
        <rect
          x={0.6} y={0.6}
          width={bodyWidth - 1.2} height={bodyHeight - 1.2}
          rx={3} ry={3}
          fill="none"
          stroke="currentColor"
          strokeWidth={1.2}
          className="text-slate-400 dark:text-slate-500"
        />
        <rect
          x={bodyWidth - 0.4}
          y={(bodyHeight - nubHeight) / 2}
          width={nubWidth} height={nubHeight}
          rx={1}
          fill="currentColor"
          className="text-slate-400 dark:text-slate-500"
        />
        <rect
          x={padding} y={padding}
          width={Math.max(fillWidth, 0)} height={innerHeight}
          rx={1.5}
          fill={fillColor}
          style={{ transition: "width 0.4s ease, fill 0.4s ease" }}
        />
        <text
          x={bodyWidth / 2}
          y={bodyHeight / 2}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize="9"
          fontWeight="700"
          fill={pct > 45 ? "#ffffff" : "#1e293b"}
          className="dark:fill-white"
          style={{ pointerEvents: "none" }}
        >
          {Math.round(pct)}
        </text>
      </svg>
    </div>
  );
};

export function RecyclingHeader({
  selectedBins,
  setSelectedBins,
  selectedWasteType,
  setSelectedWasteType,
  selectedTimePeriod,
  setSelectedTimePeriod,
  year,
  setYear,
  allBins = [],
  binDropdownOpen: externalBinDropdownOpen,
  setBinDropdownOpen: externalSetBinDropdownOpen,
  timePeriodDropdownOpen: externalTimePeriodDropdownOpen,
  setTimePeriodDropdownOpen: externalSetTimePeriodDropdownOpen,
  wasteTypeDropdownOpen: externalWasteTypeDropdownOpen,
  setWasteTypeDropdownOpen: externalSetWasteTypeDropdownOpen,
  customDateRange,
  onCustomDateRangeChange,
  isCustomDateRange
}) {
  const [internalBinDropdownOpen, setInternalBinDropdownOpen] = useState(false);
  const [internalTimePeriodDropdownOpen, setInternalTimePeriodDropdownOpen] = useState(false);
  const [internalWasteTypeDropdownOpen, setInternalWasteTypeDropdownOpen] = useState(false);
  const [availableBins, setAvailableBins] = useState([]);
  const [binsWithStatus, setBinsWithStatus] = useState([]);

  const [calendarOpen, setCalendarOpen] = useState(false);
  const [localDateRange, setLocalDateRange] = useState(customDateRange || [{
    startDate: null,
    endDate: null,
    key: 'selection'
  }]);
  const [isLocalCustomDateRange, setIsLocalCustomDateRange] = useState(isCustomDateRange || false);

  const { t } = useTranslation();

  useEffect(() => {
    if (customDateRange) setLocalDateRange(customDateRange);
    if (isCustomDateRange !== undefined) setIsLocalCustomDateRange(isCustomDateRange);
  }, [customDateRange, isCustomDateRange]);

  const getTimeRangeTranslation = (timeRange) => {
    const translations = {
      'day': t('timeRanges.Day'),
      'week': t('timeRanges.Week'),
      'month': t('timeRanges.Month'),
      '3months': t('timeRanges.3Month'),
      '6months': t('timeRanges.6Month'),
      'year': t('timeRanges.Year'),
      'Day': t('timeRanges.Day'),
      'Week': t('timeRanges.Week'),
      'Month': t('timeRanges.Month'),
      '3 Months': t('timeRanges.3Month'),
      '6 Months': t('timeRanges.6Month'),
      'Year': t('timeRanges.Year'),
      'Custom': t('timeRanges.Custom') || 'Custom'
    };
    return translations[timeRange] || timeRange;
  };

  const getTimePeriodDisplay = (period) => getTimeRangeTranslation(period);

  const formatLastSeen = (iso) => {
    const d = new Date(iso);
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${mm}${dd}-${hh}:${min}`;
  };

  // Plain bin ids (fallback / used for buildParams elsewhere via allBins prop)
  useEffect(() => {
    const fetchBinIds = async () => {
      try {
        const response = await axiosInstance.get("bin_ids");
        setAvailableBins(response.data || []);
      } catch (error) {
        console.error("RecyclingHeader: Error fetching bin IDs:", error);
      }
    };
    fetchBinIds();
  }, []);

  // Bins with status/battery/last_seen — same source and cadence as the
  // operations SiteHeader dropdown.
  useEffect(() => {
    const fetchBinStatuses = async () => {
      try {
        const response = await axiosInstance.get("bins-with-status");
        const data = response.data || [];
        const validBins = data.filter((bin) =>
          bin && typeof bin === 'object' && bin.id && typeof bin.id === 'string'
        );
        setBinsWithStatus(validBins);
      } catch (error) {
        console.error("RecyclingHeader: Error fetching bin statuses:", error);
        setBinsWithStatus([]);
      }
    };
    fetchBinStatuses();
    const interval = setInterval(fetchBinStatuses, 600000);
    return () => clearInterval(interval);
  }, []);

  const binDropdownOpen = externalBinDropdownOpen !== undefined ? externalBinDropdownOpen : internalBinDropdownOpen;
  const setBinDropdownOpen = externalSetBinDropdownOpen || setInternalBinDropdownOpen;

  const timePeriodDropdownOpen = externalTimePeriodDropdownOpen !== undefined ? externalTimePeriodDropdownOpen : internalTimePeriodDropdownOpen;
  const setTimePeriodDropdownOpen = externalSetTimePeriodDropdownOpen || setInternalTimePeriodDropdownOpen;

  // Refetch statuses whenever the dropdown opens, same as operations header
  useEffect(() => {
    if (binDropdownOpen) {
      const fetchBinStatusesForDropdown = async () => {
        try {
          const response = await axiosInstance.get("bins-with-status");
          const data = response.data || [];
          const validBins = data.filter((bin) =>
            bin && typeof bin === 'object' && bin.id && typeof bin.id === 'string'
          );
          setBinsWithStatus(validBins);
        } catch (error) {
          console.error("RecyclingHeader: Error fetching bin statuses:", error);
          setBinsWithStatus([]);
        }
      };
      fetchBinStatusesForDropdown();
    }
  }, [binDropdownOpen]);

  const fallbackBinIds = availableBins.length > 0 ? availableBins : (allBins.length > 0 ? allBins : AVAILABLE_BINS);
  const binsToDisplay = binsWithStatus.length > 0
    ? binsWithStatus
    : fallbackBinIds.map(id => ({ id, status: 'offline' }));

  const handleBinChange = (bin) => {
    setSelectedBins(prev =>
      prev.includes(bin) ? prev.filter(b => b !== bin) : [...prev, bin]
    );
  };

  const handlePeriodChange = (period) => setSelectedTimePeriod(period);

  const handleOpenChange = (open, menuType) => {
    switch (menuType) {
      case 'bin':  setBinDropdownOpen(open); break;
      case 'period': setTimePeriodDropdownOpen(open); break;
    }
  };

  const selectAllBins = () => {
    binsToDisplay.forEach(bin => {
      const binId = typeof bin === 'object' && bin !== null ? bin.id : bin;
      if (!selectedBins.includes(binId)) handleBinChange(binId);
    });
  };
  const clearBins = () => setSelectedBins([]);

  const handleDateChange = (newDateValue) => {
    setLocalDateRange(newDateValue);
    if (newDateValue?.[0]?.startDate && newDateValue?.[0]?.endDate) {
      setIsLocalCustomDateRange(true);
      setCalendarOpen(false);
      setTimePeriodDropdownOpen(false);
      if (onCustomDateRangeChange) onCustomDateRangeChange(newDateValue, true);
    }
  };

  const handleResetToTimeRange = () => {
    const resetValue = [{ startDate: null, endDate: null, key: 'selection' }];
    setLocalDateRange(resetValue);
    setIsLocalCustomDateRange(false);
    if (onCustomDateRangeChange) onCustomDateRangeChange(resetValue, false);
    if (TIME_PERIODS.length > 0) setSelectedTimePeriod(TIME_PERIODS[0]);
  };

  const getDisplayText = () => {
    if (isLocalCustomDateRange && localDateRange[0]?.startDate && localDateRange[0]?.endDate) {
      const startDate = localDateRange[0].startDate instanceof Date
        ? localDateRange[0].startDate : new Date(localDateRange[0].startDate);
      const endDate = localDateRange[0].endDate instanceof Date
        ? localDateRange[0].endDate : new Date(localDateRange[0].endDate);
      if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
        return `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`;
      }
    }
    return selectedTimePeriod ? getTimePeriodDisplay(selectedTimePeriod) : t('common.select');
  };

  return (
    <header className="group-has-data-[collapsible=icon]/sidebar-wrapper:h-12 flex h-12 shrink-0 items-center gap-2 transition-[width,height] ease-linear sticky top-0 backdrop-blur-md z-30 border-b w-full max-w-[100vw] overflow-x-hidden px-2 sm:px-4">
      <div className="flex w-full items-center justify-between gap-1">
        <div className="flex items-center min-w-0 max-w-[60%] sm:max-w-none">
          <SidebarTrigger className="-ml-1 h-8 w-8 sm:h-9 sm:w-9" />
          <Separator orientation="vertical" className="mx-1 sm:mx-2 data-[orientation=vertical]:h-4" />
          <h1 className="text-sm sm:text-base font-medium truncate">{t('navigation.circularity')}</h1>
        </div>

        <div className="flex gap-1 sm:gap-2">

          {/* Bin Filter — same dropdown as operations SiteHeader (status dot / battery badge / last-seen) */}
          <DropdownMenu modal={false} open={binDropdownOpen} onOpenChange={(open) => handleOpenChange(open, 'bin')}>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-8 px-2 text-xs sm:h-9 sm:px-3 sm:text-sm">
                <Trash className="h-3 w-3 sm:h-4 sm:w-4 opacity-50 mr-1" />
                <span className="hidden sm:inline">{t('header.filters.bins')}: </span>
                {selectedBins.length}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56">
              <div className="flex items-center justify-between px-2 py-1.5">
                <DropdownMenuLabel className="py-0">{t('header.filters.selected')}</DropdownMenuLabel>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-xs"
                    onClick={(e) => { e.preventDefault(); selectAllBins(); }}
                  >
                    <Check className="h-3 w-3 mr-1" />
                    {t('common.all')}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-xs"
                    onClick={(e) => { e.preventDefault(); clearBins(); }}
                  >
                    <X className="h-3 w-3 mr-1" />
                    {t('common.clear')}
                  </Button>
                </div>
              </div>
              <DropdownMenuSeparator />
              {[...binsToDisplay]
                .filter(bin => bin != null)
                .sort((a, b) => {
                  const binA = typeof a === 'object' && a ? a.id : a;
                  const binB = typeof b === 'object' && b ? b.id : b;
                  if (!binA || !binB) return 0;
                  const binAStr = String(binA);
                  const binBStr = String(binB);
                  const nameA = binAStr.replace(/\d+/g, '').toLowerCase();
                  const nameB = binBStr.replace(/\d+/g, '').toLowerCase();
                  if (nameA < nameB) return -1;
                  if (nameA > nameB) return 1;
                  const numA = parseInt(binAStr.match(/\d+/)?.[0] || '0');
                  const numB = parseInt(binBStr.match(/\d+/)?.[0] || '0');
                  return numA - numB;
                })
                .map(bin => {
                  const binId = typeof bin === 'object' && bin ? bin.id : bin;
                  const binStatus = typeof bin === 'object' && bin ? bin.status || 'offline' : 'offline';
                  if (!binId) return null;
                  return (
                    <DropdownMenuCheckboxItem
                      key={binId}
                      checked={selectedBins.includes(binId)}
                      onSelect={(e) => { e.preventDefault(); handleBinChange(binId); }}
                      className="flex items-center justify-center py-2 pl-2 pr-2 [&>span]:hidden"
                    >
                      <div className="flex flex-col items-center w-full">
                        <div className="grid grid-cols-[24px_1fr_36px] items-center gap-2 w-fit min-w-[130px]">

                          {/* Checkmark */}
                          <div className="flex justify-center">
                            {selectedBins.includes(binId) && (
                              <Check className="h-4 w-4 text-primary shrink-0" />
                            )}
                          </div>

                          {/* Bin ID */}
                          <span className="text-center font-semibold text-sm truncate">
                            {binId}
                          </span>

                          {/* Status OR Battery */}
                          <div className="flex justify-center">
                            {typeof bin === 'object' && bin && bin.battery_percentage != null && bin.battery_color ? (
                              <BatteryBadge percentage={bin.battery_percentage} color={bin.battery_color} />
                            ) : (
                              <Status status={binStatus}>
                                <StatusIndicator />
                              </Status>
                            )}
                          </div>
                        </div>

                        {binStatus === 'offline' && typeof bin === 'object' && bin && bin.last_seen && (
                          <span className="text-[12px] text-muted-foreground whitespace-nowrap leading-tight mt-1 opacity-80">
                            Since {formatLastSeen(bin.last_seen)}
                          </span>
                        )}
                      </div>
                    </DropdownMenuCheckboxItem>
                  );
                })
                .filter(item => item !== null)
              }
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Time Period Filter with Calendar */}
          <DropdownMenu modal={false} open={timePeriodDropdownOpen} onOpenChange={(open) => handleOpenChange(open, 'period')}>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-8 px-2 text-xs sm:h-9 sm:px-3 sm:text-sm">
                <Clock className="h-3 w-3 sm:h-4 sm:w-4 opacity-50 mr-1" />
                <span className="hidden sm:inline truncate max-w-[200px]">
                  {t('header.filters.period')}: {getDisplayText()}
                </span>
                <span className="sm:hidden truncate max-w-[100px]">{getDisplayText()}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56" onCloseAutoFocus={(e) => e.preventDefault()}>
              <div className="flex items-center justify-between px-2 py-1.5">
                <DropdownMenuLabel className="py-0">{t('header.filters.selectTimePeriod')}</DropdownMenuLabel>
                {isLocalCustomDateRange && (
                  <Button variant="ghost" size="sm" className="h-6 px-2 text-xs"
                    onClick={(e) => { e.preventDefault(); handleResetToTimeRange(); }}>
                    <X className="h-3 w-3 mr-1"/>
                    {t('reset') || 'Reset'}
                  </Button>
                )}
              </div>
              <DropdownMenuSeparator />

              <DropdownMenuCheckboxItem
                checked={isLocalCustomDateRange}
                onSelect={(e) => { e.preventDefault(); setCalendarOpen(true); }}
              >
                <Calendar className="h-4 w-4 mr-2"/>
                {t('selectDateRange') || 'Custom Date Range'}
              </DropdownMenuCheckboxItem>

              <DropdownMenuSeparator />

              {TIME_PERIODS.map(period => (
                <DropdownMenuCheckboxItem
                  key={period}
                  checked={!isLocalCustomDateRange && selectedTimePeriod === period}
                  onSelect={(e) => {
                    e.preventDefault();
                    handleResetToTimeRange();
                    handlePeriodChange(period);
                  }}
                >
                  {getTimePeriodDisplay(period)}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Calendar Dialog */}
          <Dialog open={calendarOpen} onOpenChange={setCalendarOpen}>
            <DialogContent className="sm:max-w-5xl w-full">
              <DialogHeader>
                <DialogTitle>{t('selectDateRange') || 'Select Date Range'}</DialogTitle>
              </DialogHeader>
              <CustomCalendar datevalue={localDateRange} onDateValueChange={handleDateChange} />
            </DialogContent>
          </Dialog>

        </div>
      </div>
    </header>
  );
}